import * as React from 'react';
import { useEffect, useState } from 'react';
import Dropdown from 'reactstrap/lib/Dropdown';
import DropdownItem from 'reactstrap/lib/DropdownItem';
import DropdownMenu from 'reactstrap/lib/DropdownMenu';
import DropdownToggle from 'reactstrap/lib/DropdownToggle';
import { useDebouncedCallback } from 'use-debounce';
import { searchLocations, LocationSearchResults } from '../../../../../api/LocationSearch';
import { useAsyncReference } from '../../../../../hooks/useAsyncReference';
import { intersperse } from '../../../../../utilities/intersperse';
import { getStateName } from '../../../../../utilities/stateAbbreviations';
import { flattenLocationSearchResults, PropertyQuery } from './PropertyQuery';
import SearchResultDropdownItem from './SearchResultDropdownItem';

interface Props {
    valueSelected: (value: PropertyQuery) => void;
    setFirstSuggestion?: (firstSuggestion: PropertyQuery | undefined) => void;
}

const PropertySearchbar = (props: Props) => {
    const [searchTermsReference, setSearchTerms] = useAsyncReference('');
    const [suggestions, setSuggestions] = useState<LocationSearchResults>();
    const [errorMessage, setErrorMessage] = useState<string>();
    const [displaySuggestions, setDisplaySuggestions] = useState(false);

    const searchIcon = require('../../../../../images/search.svg');

    const resultsList = suggestions && flattenLocationSearchResults(suggestions);
    const firstSuggestion = (resultsList && resultsList.length > 0 && resultsList[0]) || undefined;

    useEffect(() => {
        if (props.setFirstSuggestion) {
            props.setFirstSuggestion(firstSuggestion && firstSuggestion.propertyQuery);
        }
    }, [firstSuggestion]);

    const handleSearch = async (searchText: string) => {
        if (suggestions) {
            // clear existing suggestions so user knows it's loading
            setDisplaySuggestions(false);
        }

        let suggestionsResult = await searchLocations(searchText);

        if (!suggestionsResult.success || !suggestionsResult.data) {
            setErrorMessage(suggestionsResult.error || 'Error while fetching suggestions');
            return;
        }

        let suggestionsObject = suggestionsResult.data;

        if (searchText !== searchTermsReference.current) {
            return;
        }

        setDisplaySuggestions(true);
        setSuggestions(suggestionsObject);
    };

    const debouncedHandleSearch = useDebouncedCallback(handleSearch, 250);

    const allExistingResultsMatchNewSearch = (newSearchText: string) => {
        const searchString = newSearchText.toLowerCase();
        return (
            resultsList &&
            resultsList.length > 0 &&
            resultsList.every(
                s =>
                    getStateName(s.propertyQuery.stateAbbreviation)
                        .toLowerCase()
                        .includes(searchString) ||
                    (s.propertyQuery.cityName &&
                        s.propertyQuery.cityName.toLowerCase().includes(searchString)) ||
                    (s.propertyQuery.propertyName &&
                        s.propertyQuery.propertyName.toLowerCase().includes(searchString)) ||
                    false
            )
        );
    };

    const onSearchTextChange = (searchText: string) => {
        if (
            searchText.length < 3 ||
            (searchText.length > searchTermsReference.current.length &&
                allExistingResultsMatchNewSearch(searchText))
        ) {
            // if the new search is too short or matches all existing results, don't do anything
            return;
        } else if (suggestions && searchText.length < 3) {
            setSuggestions(undefined);
        }
        debouncedHandleSearch.callback(searchText);
    };

    const showSuggestions =
        displaySuggestions &&
        resultsList &&
        searchTermsReference.current.length > 2 &&
        resultsList.length > 0;

    return (
        <div className="property-searchbar-container">
            <div className="icon-input-container">
                <input
                    id="search"
                    name="search"
                    placeholder="Enter property, city, state or zip"
                    type="search"
                    onChange={e => {
                        onSearchTextChange(e.target.value);
                        setSearchTerms(e.target.value);
                    }}
                    onKeyUp={e => {
                        if (e.key === 'Enter' && firstSuggestion) {
                            props.valueSelected(firstSuggestion.propertyQuery);
                        }
                    }}
                    value={searchTermsReference.current}
                    className="icon-input form-control"
                    autoComplete="off"
                />
                <div className="icon-container">
                    <img
                        src={searchIcon}
                        className={`search-icon-${
                            firstSuggestion ? 'enabled' : 'disabled'
                        } valid-or-invalid-icon`}
                        onClick={() => {
                            if (firstSuggestion) {
                                props.valueSelected(firstSuggestion.propertyQuery);
                            }
                        }}
                    />
                </div>
            </div>
            <div>
                <Dropdown
                    toggle={() => {
                        setDisplaySuggestions(false);
                    }}
                    isOpen={showSuggestions || errorMessage !== undefined}
                >
                    <DropdownToggle className="toggle-button" />
                    <DropdownMenu>
                        {resultsList && showSuggestions ? (
                            intersperse(
                                resultsList
                                    .slice(0, 5)
                                    .map((r, index) => (
                                        <SearchResultDropdownItem
                                            text={r.display}
                                            key={index}
                                            searchTerms={searchTermsReference.current}
                                            handleClick={() => props.valueSelected(r.propertyQuery)}
                                        />
                                    )),
                                index => <DropdownItem key={-index} divider={true} />
                            )
                        ) : (
                            <DropdownItem className="dropdown-item-error-message">
                                {errorMessage}
                            </DropdownItem>
                        )}
                    </DropdownMenu>
                </Dropdown>
            </div>
        </div>
    );
};

export default PropertySearchbar;



// WEBPACK FOOTER //
// ./src/components/pages/prospect/public/properties/PropertySearchbar.tsx